import { supabase, run, runAllPages, runOrDefault } from './db';
import { cached } from './local/cache';
import { queueable } from './local/queued';
import { filtroDeCruzamentos } from '../utils/animais';

export const fetchAnimal = (animalId) =>
  run(supabase.from('animals').select('*').eq('id', animalId).single());

/** Animais do laboratório, em ordem alfabética de identificação. */
export const listAnimalsByIdentifier = (laboratoryId) =>
  cached(`animals:${laboratoryId}`, () => runAllPages(() => supabase
    .from('animals')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .order('identifier', { ascending: true })
    .order('id', { ascending: true })));

/** Animais do laboratório, mais recentes primeiro, com filtros opcionais. */
export const listAnimals = (laboratoryId, filters = {}) => {
  let query = supabase.from('animals').select('*').eq('laboratory_id', laboratoryId);
  if (filters.category) query = query.eq('category', filters.category);
  if (filters.client_id) query = query.eq('client_id', filters.client_id);
  return run(query.order('created_at', { ascending: false }));
};

/** Busca por identificação/RGD; a função no banco cuida do escopo do laboratório. */
export const searchAnimals = (searchTerm, laboratoryId) =>
  cached(`busca-animais:${laboratoryId}:${searchTerm}`,
    () => run(supabase.rpc('search_animals', { search_term: searchTerm, lab_id: laboratoryId })));

/**
 * Cadastra e devolve o animal.
 *
 * Quem chama usa a linha de volta (para vincular a uma doadora, por exemplo),
 * então offline não dá para devolver `QUEUED` e pronto. Como o id é gerado no
 * aparelho, a linha que devolvemos é a mesma que o servidor vai gravar — e o
 * chamador segue o fluxo sem saber se já subiu ou não.
 */
export const insertAnimal = queueable('animal.insert.retorna',
  (row) => run(supabase.from('animals').insert([row]).select().single()),
  {
    patchOf: (row) => ({
      chave: `animals:${row.laboratory_id}`,
      tipo: 'insere',
      linha: row,
      ordenarPor: 'identifier',
    }),
  });

/** Cadastro rápido em campo. Id gerado no aparelho, pelo mesmo motivo dos serviços. */
export const insertAnimalRow = queueable('animal.insert',
  (row) => run(supabase.from('animals').insert([row])),
  {
    patchOf: (row) => ({
      chave: `animals:${row.laboratory_id}`,
      tipo: 'insere',
      linha: row,
      ordenarPor: 'identifier',
    }),
  });

export const newAnimalId = () => crypto.randomUUID();

export const updateAnimal = queueable('animal.update.retorna',
  (animalId, updates) =>
    run(supabase.from('animals').update(updates).eq('id', animalId).select().single()),
  {
    patchOf: (animalId, updates, laboratoryId) => (laboratoryId ? {
      chave: `animals:${laboratoryId}`, tipo: 'atualiza',
      id: animalId, campos: updates, ordenarPor: 'identifier',
    } : null),
  });

export const updateAnimalRow = queueable('animal.update',
  (animalId, updates, laboratoryId) =>
    run(supabase.from('animals').update(updates).eq('id', animalId)),
  {
    patchOf: (animalId, updates, laboratoryId) => (laboratoryId ? {
      chave: `animals:${laboratoryId}`,
      tipo: 'atualiza',
      id: animalId,
      campos: updates,
      ordenarPor: 'identifier',
    } : null),
  });

export const deleteAnimal = queueable('animal.delete',
  (animalId) => run(supabase.from('animals').delete().eq('id', animalId)),
  {
    patchOf: (animalId, laboratoryId) => (laboratoryId ? {
      chave: `animals:${laboratoryId}`, tipo: 'remove', id: animalId,
    } : null),
  });

/** RGD dos animais informados — leitura acessória, usada para enfeitar telas e PDFs. */
export const fetchAnimalsRgd = (ids) =>
  runOrDefault(supabase.from('animals').select('id, rgd').in('id', ids), []);

/** Histórico de transferências de uma receptora. */
export const fetchRecipientHistory = (animalId) =>
  run(supabase
    .from('embryos')
    .select('*')
    .eq('recipient_id', animalId)
    .order('transfer_date', { ascending: false }));

/**
 * Transferências em que o animal entrou — como touro, doadora ou receptora —,
 * com as datas de aspiração, FIV e TE. Mais recentes primeiro.
 *
 * Sem cache de propósito: abre-se a ficha para conferir o que acabou de ser
 * lançado na TE.
 */
export const listarCruzamentos = (animal, laboratoryId) => {
  const filtro = filtroDeCruzamentos(animal);
  if (!filtro) return Promise.resolve([]);
  return runAllPages(() => supabase
    .from('vw_cruzamentos')
    .select('*')
    .eq('laboratory_id', laboratoryId)
    .or(filtro)
    .order('te', { ascending: false })
    .order('te_transfer_id', { ascending: true }));
};
